import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { COMPONENT_META } from '@/workspace/components/add-panel-menu-constants'
import { Keys } from './keycap'
import { SHORTCUT_PAGES } from './shortcuts-catalog'
import type { ShortcutSection } from './shortcuts-catalog'

function SectionBlock({ section }: { section: ShortcutSection }) {
  const meta = section.panelType ? COMPONENT_META[section.panelType] : undefined
  const Icon = meta?.icon
  const title = section.title ?? meta?.label ?? ''
  return (
    <div className="mb-5 break-inside-avoid">
      <div className="mb-2 flex items-center gap-1.5">
        {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground" />}
        <span className="text-xs font-semibold uppercase tracking-wider text-foreground">
          {title}
        </span>
      </div>
      <div className="flex flex-col gap-1.5">
        {section.rows.map((row, i) => (
          <div key={i} className="flex items-center justify-between gap-4">
            <span className="text-sm text-muted-foreground">{row.action}</span>
            <Keys tokens={row.keys} />
          </div>
        ))}
      </div>
    </div>
  )
}

interface ShortcutsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Which page tab to open on — set from the current route so opening from
   *  page A lands on page A's tab. */
  defaultPageKey?: string
}

/**
 * Read-only keyboard-shortcuts cheat sheet. Page tabs across the top; each page
 * shows a General section plus one section per panel type on that page. Esc / ✕
 * close it (Radix Dialog default). Remapping is a future phase — this is the
 * reference view.
 */
export function ShortcutsDialog({ open, onOpenChange, defaultPageKey }: ShortcutsDialogProps) {
  const [tab, setTab] = useState(defaultPageKey ?? SHORTCUT_PAGES[0].key)

  // Re-sync to the caller's page each time the dialog opens.
  useEffect(() => {
    if (open && defaultPageKey) setTab(defaultPageKey)
  }, [open, defaultPageKey])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Keyboard Hotkeys</DialogTitle>
          <DialogDescription className="sr-only">
            Keyboard shortcuts by page and panel.
          </DialogDescription>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="flex-wrap">
            {SHORTCUT_PAGES.map((p) => (
              <TabsTrigger key={p.key} value={p.key}>
                {p.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {SHORTCUT_PAGES.map((p) => (
            <TabsContent key={p.key} value={p.key} className="mt-4 max-h-[60vh] overflow-y-auto">
              <div className="gap-8 sm:columns-2 lg:columns-3">
                {p.sections.map((s, i) => (
                  <SectionBlock key={i} section={s} />
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}
